import { getMessages, formatDecimal, formatInteger } from "@/i18n/runtime";
import { formatForContext } from "@/features/hoverIdle/resources/numberFormat";
import { HovCoreHudView } from "@/features/hoverIdle/oHov/hovCoreHudView";
import { createMpSlotIndicator } from "@/features/hoverIdle/mpm/mpSlotView";
import { MpSlotChargeController } from "@/features/hoverIdle/mpm/mpSlotChargeController";
import {
  createEnabledHoverToggle,
  type EnabledHoverToggle,
} from "@/features/hoverIdle/mpm/enabledHoverToggle";
import { HoverIntentController } from "@/features/hoverIdle/core/hoverIntentController";
import type { HoverPointerModel } from "@/features/hoverIdle/input/hoverPointerModel";
import {
  createUpgradeTooltip,
  type UpgradeTooltipElements,
} from "@/features/hoverIdle/upgrades/upgradeTooltipView";
import { UpgradeInteractionController } from "@/features/hoverIdle/upgrades/upgradeInteractionController";
import type { Disposable } from "@/shared/contracts/lifecycle";
import type { HoverIdleSnapshot } from "@/features/hoverIdle/types";
import clockIconSource from "bootstrap-icons/icons/clock.svg?raw";
import type {
  ExplorationAreaDetailsSnapshot,
  ExplorationBiomeId,
  ExplorationDetailsSnapshot,
  ExplorationEffect,
  ExplorationModelCommandResult,
  ExplorationMpSlotDetailsSnapshot,
  ExplorationMpSlotId,
} from "./explorationTypes";

export interface ExplorationScreenSnapshot {
  readonly gameplay: HoverIdleSnapshot;
  readonly exploration: ExplorationDetailsSnapshot;
}

export interface ExplorationViewOptions {
  readonly container: HTMLElement;
  readonly initialSnapshot: ExplorationScreenSnapshot;
  readonly onPurchaseMpSlot: () => ExplorationModelCommandResult;
  readonly onAssignMpSlot: (
    slotId: ExplorationMpSlotId,
  ) => ExplorationModelCommandResult;
  readonly onRemoveMpSlot: (
    slotId: ExplorationMpSlotId,
  ) => ExplorationModelCommandResult;
  readonly pointerModel: HoverPointerModel;
  readonly getHoverToggleDurationMs: () => number;
  readonly onBiomeHoverChange: (
    biomeId: ExplorationBiomeId,
    isHovering: boolean,
  ) => boolean | void;
  readonly onTimAccelerationEnabledChange: (
    enabled: boolean,
  ) => ExplorationDetailsSnapshot;
}

interface ExplorationSlotElements {
  readonly root: HTMLButtonElement;
  readonly indicator: HTMLElement;
  readonly controller: MpSlotChargeController;
}

interface ExplorationAreaElements {
  readonly root: HTMLElement;
  readonly heading: HTMLElement;
  readonly effect: HTMLElement;
  readonly hoverIntent: HoverIntentController;
  readonly slotsRoot: HTMLElement;
  readonly slots: Map<ExplorationMpSlotId, ExplorationSlotElements>;
}

export class ExplorationView implements Disposable {
  readonly root: HTMLDivElement;
  private readonly hovCoreHud = new HovCoreHudView({
    secondaryResource: "ohov-power",
    showRecentRate: false,
    showHovGain: true,
  });
  private readonly mpUsageValue: HTMLElement;
  private readonly controlsRow: HTMLElement;
  private readonly mpSlotPurchaseButton: HTMLButtonElement;
  private readonly mpSlotPurchasePrice: HTMLElement;
  private readonly mpSlotPurchaseCard: HTMLElement;
  private readonly mpSlotPurchaseTooltip: UpgradeTooltipElements;
  private readonly mpSlotPurchaseInteraction: UpgradeInteractionController;
  private readonly timAccelerationControl: HTMLElement;
  private readonly timAccelerationTitle: HTMLElement;
  private readonly timAccelerationDescription: HTMLElement;
  private readonly timAccelerationToggle: EnabledHoverToggle;
  private readonly areas = new Map<
    ExplorationBiomeId,
    ExplorationAreaElements
  >();
  private readonly options: ExplorationViewOptions;
  private currentTim: HoverIdleSnapshot["tim"];

  constructor(options: ExplorationViewOptions) {
    this.options = options;
    const messages = getMessages().exploration;
    this.root = document.createElement("div");
    this.root.className = "hover-idle-exploration-screen";
    this.root.setAttribute("aria-label", messages.regionAria);

    const statusRow = document.createElement("div");
    statusRow.className =
      "hover-idle-hud-cluster hover-idle-exploration-status-row";
    this.hovCoreHud.root.dataset.explorationStatus = "resources";
    const mpUsage = document.createElement("section");
    mpUsage.className = "hover-idle-hud-card hover-idle-exploration-mp-usage";
    mpUsage.dataset.explorationStatus = "mp";
    mpUsage.setAttribute("role", "status");
    mpUsage.setAttribute("aria-live", "polite");
    mpUsage.setAttribute("aria-label", messages.mpUsageAria);
    this.mpUsageValue = document.createElement("strong");
    this.mpUsageValue.className = "hover-idle-hud-card__primary";
    this.mpUsageValue.dataset.explorationField = "mp-usage";
    mpUsage.append(this.mpUsageValue);
    statusRow.append(this.hovCoreHud.root, mpUsage);

    this.controlsRow = document.createElement("div");
    this.controlsRow.className = "hover-idle-exploration-controls";
    this.controlsRow.dataset.explorationControls = "true";
    const timAcceleration = this.createTimAccelerationControl();
    this.timAccelerationControl = timAcceleration.control;
    this.timAccelerationTitle = timAcceleration.title;
    this.timAccelerationDescription = timAcceleration.description;
    this.timAccelerationToggle = timAcceleration.toggle;
    this.controlsRow.append(timAcceleration.control);

    const purchaseControl = document.createElement("section");
    purchaseControl.className = "hover-idle-exploration-purchase-control";
    purchaseControl.dataset.explorationControl = "mp-slot-purchase";
    this.mpSlotPurchaseButton = document.createElement("button");
    this.mpSlotPurchaseButton.type = "button";
    this.mpSlotPurchaseButton.className =
      "hover-idle-exploration-purchase-button";
    this.mpSlotPurchaseButton.dataset.explorationMpSlotPurchase = "true";
    this.mpSlotPurchaseButton.dataset.virtualPointer = "allow";
    this.mpSlotPurchasePrice = document.createElement("span");
    this.mpSlotPurchasePrice.className =
      "hover-idle-exploration-purchase-price";
    this.mpSlotPurchaseButton.append(this.mpSlotPurchasePrice);

    this.mpSlotPurchaseCard = document.createElement("div");
    this.mpSlotPurchaseCard.className =
      "hover-idle-upgrade-card hover-idle-exploration-purchase-card";
    this.mpSlotPurchaseCard.dataset.explorationMpSlotInfo = "true";
    this.mpSlotPurchaseCard.tabIndex = 0;
    this.mpSlotPurchaseCard.setAttribute(
      "aria-label",
      messages.mpSlotPurchase.tooltipName,
    );
    this.mpSlotPurchaseCard.textContent = messages.mpSlotPurchase.name;

    const tooltipId = "hover-idle-exploration-mp-slot-tooltip";
    this.mpSlotPurchaseCard.setAttribute("aria-describedby", tooltipId);
    this.mpSlotPurchaseTooltip = createUpgradeTooltip({
      id: tooltipId,
      kicker: messages.mpSlotPurchase.tooltipKind,
      title: messages.mpSlotPurchase.tooltipName,
      region: messages.title,
      summary: messages.mpSlotPurchase.description,
      afterLabel: "",
    });
    this.mpSlotPurchaseTooltip.root.dataset.explorationMpSlotTooltip = "true";
    this.mpSlotPurchaseTooltip.afterRow.hidden = true;
    this.mpSlotPurchaseTooltip.afterRow.dataset.explorationTooltipAfter =
      "true";
    purchaseControl.append(
      this.mpSlotPurchaseButton,
      this.mpSlotPurchaseCard,
      this.mpSlotPurchaseTooltip.root,
    );
    this.controlsRow.append(purchaseControl);

    this.mpSlotPurchaseInteraction = new UpgradeInteractionController({
      button: this.mpSlotPurchaseButton,
      card: this.mpSlotPurchaseCard,
      tooltip: this.mpSlotPurchaseTooltip.root,
      pointerModel: this.options.pointerModel,
      onUpgrade: () => {
        if (this.mpSlotPurchaseButton.disabled) return false;
        const result = this.options.onPurchaseMpSlot();
        this.applyCommandResult(result);
        return result.ok;
      },
    });

    const areasRoot = document.createElement("div");
    areasRoot.className = "hover-idle-exploration-areas";
    for (const biomeId of options.initialSnapshot.exploration.biomeIds) {
      const area = this.createArea(biomeId);
      this.areas.set(biomeId, area);
      areasRoot.append(area.root);
    }

    this.root.append(statusRow, this.controlsRow, areasRoot);
    options.container.append(this.root);

    this.currentTim = options.initialSnapshot.gameplay.tim;
    this.update(options.initialSnapshot);
  }

  update(snapshot: ExplorationScreenSnapshot): void {
    this.currentTim = snapshot.gameplay.tim;
    this.hovCoreHud.update(snapshot.gameplay);
    this.syncExploration(snapshot.exploration);
  }

  dispose(): void {
    for (const area of this.areas.values()) {
      for (const slot of area.slots.values()) {
        slot.controller.dispose();
      }
      area.hoverIntent.dispose();
    }
    this.hovCoreHud.dispose();
    this.mpSlotPurchaseInteraction.dispose();
    this.timAccelerationToggle.dispose();
    this.root.remove();
  }

  setVisible(visible: boolean): void {
    this.mpSlotPurchaseInteraction.setInputLocked(!visible);
    for (const area of this.areas.values()) {
      area.hoverIntent.setEnabled(visible);
      if (visible) {
        area.hoverIntent.refresh({ forceEmit: true });
      }
      for (const slot of area.slots.values()) {
        if (visible) {
          slot.controller.refresh({ forceEmit: true });
        } else {
          slot.controller.clearPointerHover();
        }
      }
    }
    if (!visible) {
      this.timAccelerationToggle.clearPointerHover();
    }
  }

  restoreVirtualPointerFromHoverAnchor(): boolean {
    return this.options.pointerModel.restoreVirtualTouchAnchorPosition(
      (targetId) => {
        for (const area of this.areas.values()) {
          if (area.root.dataset.hoverAnchorTargetId === targetId) {
            return area.root.getBoundingClientRect();
          }
        }
        return null;
      },
    );
  }

  private createTimAccelerationControl(): {
    readonly control: HTMLElement;
    readonly title: HTMLElement;
    readonly description: HTMLElement;
    readonly toggle: EnabledHoverToggle;
  } {
    const messages = getMessages().exploration.timAcceleration;
    const control = document.createElement("section");
    control.className = "hover-idle-exploration-tim-acceleration";
    control.dataset.explorationControl = "tim-acceleration";
    control.dataset.state = "locked";

    const content = document.createElement("div");
    content.className = "hover-idle-exploration-tim-acceleration-content";
    const title = document.createElement("strong");
    const description = document.createElement("span");
    description.className =
      "hover-idle-exploration-tim-acceleration-description";
    content.append(title, description);

    const toggle = createEnabledHoverToggle({
      pointerModel: this.options.pointerModel,
      action: "exploration-tim-acceleration",
      ariaLabel: messages.toggleAria(false, false),
      additionalClassName: "hover-idle-exploration-tim-acceleration-action",
      iconSource: clockIconSource,
      getChargeMs: this.options.getHoverToggleDurationMs,
      onEnabledChange: (enabled) => {
        this.syncExploration(
          this.options.onTimAccelerationEnabledChange(enabled),
        );
      },
    });

    control.append(toggle.element, content);
    return { control, title, description, toggle };
  }

  private createArea(biomeId: ExplorationBiomeId): ExplorationAreaElements {
    const messages = getMessages().exploration;
    const biomeMessages = messages.biomes[biomeId];
    const root = document.createElement("article");
    root.className = "hover-idle-exploration-area";
    root.dataset.explorationArea = "true";
    root.dataset.explorationBiome = biomeId;
    root.dataset.explorationSurface = biomeId;
    root.dataset.hoverAnchorTargetId = `exploration-${biomeId}`;
    root.setAttribute("data-hover-anchor-target-id", `exploration-${biomeId}`);
    root.setAttribute("aria-label", messages.areaAria(biomeMessages.name));
    root.tabIndex = 0;

    const header = document.createElement("header");
    header.className = "hover-idle-exploration-area-header";
    const heading = document.createElement("h3");
    heading.dataset.explorationHeading = biomeId;
    const effect = document.createElement("span");
    effect.className = "hover-idle-exploration-area-effect";
    effect.dataset.explorationEffect = biomeId;
    header.append(heading, effect);

    const hoverIntent = new HoverIntentController({
      target: root,
      pointerModel: this.options.pointerModel,
      initiallyEnabled: false,
      onHoverChange: (isHovering) =>
        this.options.onBiomeHoverChange(biomeId, isHovering),
    });

    const slotsRoot = document.createElement("div");
    slotsRoot.className = "hover-idle-exploration-mp-slots";
    slotsRoot.dataset.explorationSlots = biomeId;

    root.append(header, slotsRoot);
    return {
      root,
      heading,
      effect,
      hoverIntent,
      slotsRoot,
      slots: new Map(),
    };
  }

  private createSlot(
    slot: ExplorationMpSlotDetailsSnapshot,
  ): ExplorationSlotElements {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "hover-idle-exploration-mp-slot-control";
    button.dataset.explorationMpSlot = slot.id;
    button.dataset.explorationBiome = slot.biomeId;
    button.dataset.virtualPointer = "allow";
    const indicator = createMpSlotIndicator({
      requiredMp: slot.amount,
      pauseOverlay: true,
    });
    button.append(indicator);

    const controller = new MpSlotChargeController({
      element: button,
      stateElement: indicator,
      pointerModel: this.options.pointerModel,
      getChargeMs: this.options.getHoverToggleDurationMs,
      onAssign: () =>
        this.applyCommandResult(this.options.onAssignMpSlot(slot.id)),
      onRemove: () =>
        this.applyCommandResult(this.options.onRemoveMpSlot(slot.id)),
    });
    const elements: ExplorationSlotElements = {
      root: button,
      indicator,
      controller,
    };
    return elements;
  }

  private updateArea(
    elements: ExplorationAreaElements,
    area: ExplorationAreaDetailsSnapshot,
    biomeLevel: number,
  ): void {
    const messages = getMessages().exploration;
    const biomeMessages = messages.biomes[area.id];
    const progressRatio =
      area.periodMs > 0
        ? Math.max(0, Math.min(1, area.progressMs / area.periodMs))
        : 0;
    elements.root.dataset.running = area.isRunning ? "true" : "false";
    elements.root.dataset.progressMs = String(area.progressMs);
    elements.root.style.setProperty(
      "--exploration-progress-turn",
      `${formatProgressTurn(progressRatio)}turn`,
    );
    elements.heading.textContent = messages.biomeHeading(
      biomeMessages.name,
      formatInteger(biomeLevel),
    );
    elements.effect.textContent = messages.effectLabel(
      formatExplorationEffect(area.currentEffect),
    );
    this.syncAreaSlots(elements, area.mpSlots);
  }

  private syncAreaSlots(
    elements: ExplorationAreaElements,
    slots: readonly ExplorationMpSlotDetailsSnapshot[],
  ): void {
    const expectedIds = new Set(slots.map((slot) => slot.id));
    for (const [slotId, slotElements] of elements.slots) {
      if (expectedIds.has(slotId)) continue;
      slotElements.controller.dispose();
      slotElements.root.remove();
      elements.slots.delete(slotId);
    }

    for (const slot of slots) {
      let slotElements = elements.slots.get(slot.id);
      if (slotElements === undefined) {
        slotElements = this.createSlot(slot);
        elements.slots.set(slot.id, slotElements);
        elements.slotsRoot.append(slotElements.root);
      }
      this.updateSlot(slotElements, slot);
    }
  }

  private updateSlot(
    elements: ExplorationSlotElements,
    slot: ExplorationMpSlotDetailsSnapshot,
  ): void {
    const messages = getMessages().exploration;
    const availability = slot.assigned
      ? slot.removeAvailability
      : slot.assignAvailability;
    elements.root.disabled = !availability.canExecute;
    elements.root.setAttribute(
      "aria-disabled",
      availability.canExecute ? "false" : "true",
    );
    elements.root.dataset.selected = slot.assigned ? "true" : "false";
    const operative = slot.assigned && !slot.pausedByMpm;
    elements.root.dataset.mpAssigned = slot.assigned ? "true" : "false";
    elements.root.dataset.mpOperative = operative ? "true" : "false";
    elements.root.dataset.paused = slot.pausedByMpm ? "true" : "false";
    elements.root.dataset.hiddenByMpm = slot.hiddenByMpm ? "true" : "false";
    if (slot.hiddenByMpm) {
      elements.root.setAttribute("aria-hidden", "true");
    } else {
      elements.root.removeAttribute("aria-hidden");
    }
    const insufficientMp =
      !slot.assigned &&
      !availability.canExecute &&
      availability.reason === "global-mp-capacity-exceeded";
    elements.root.dataset.availability = slot.assigned
      ? "assigned"
      : availability.canExecute
        ? "available"
        : insufficientMp
          ? "insufficient-mp"
          : "blocked";
    elements.root.dataset.disabled =
      !slot.assigned && !availability.canExecute ? "true" : "false";
    elements.indicator.dataset.state = slot.assigned ? "filled" : "empty";
    elements.indicator.dataset.disabled =
      !slot.assigned && !availability.canExecute ? "true" : "false";
    elements.indicator.dataset.mpAssigned = slot.assigned ? "true" : "false";
    elements.indicator.dataset.mpOperative = operative ? "true" : "false";
    elements.indicator.dataset.paused = slot.pausedByMpm ? "true" : "false";
    elements.indicator.dataset.mpBlocked = "false";
    elements.indicator.dataset.blocked = "false";
    elements.controller.setInputLocked(
      slot.hiddenByMpm || !availability.canExecute,
    );
    elements.controller.sync({
      isAssigned: slot.assigned,
      canAssign: slot.assignAvailability.canExecute,
      canRemove: slot.removeAvailability.canExecute,
    });
    const amount = formatInteger(slot.amount);
    const ariaLabel = slot.assigned
      ? messages.removeMpSlotAria(amount)
      : messages.assignMpSlotAria(amount);
    elements.root.setAttribute("aria-label", ariaLabel);
    if (availability.canExecute) {
      elements.root.setAttribute("title", ariaLabel);
      delete elements.root.dataset.blockedReason;
    } else {
      const reason = messages.blockedReasons[availability.reason];
      elements.root.setAttribute("title", reason);
      elements.root.dataset.blockedReason = availability.reason;
    }
  }

  private applyCommandResult(result: ExplorationModelCommandResult): void {
    this.syncExploration(result.snapshot);
  }

  private syncExploration(snapshot: ExplorationDetailsSnapshot): void {
    this.mpUsageValue.textContent = getMessages().exploration.mpUsage(
      formatInteger(snapshot.mp.total - snapshot.mp.used),
      formatInteger(snapshot.mp.total),
    );
    this.syncMpSlotPurchase(snapshot);
    this.syncTimAcceleration(snapshot);
    for (const [biomeId, elements] of this.areas) {
      this.updateArea(elements, snapshot.areas[biomeId], snapshot.biomeLevel);
    }
  }

  private syncTimAcceleration(snapshot: ExplorationDetailsSnapshot): void {
    const acceleration = snapshot.timAcceleration;
    const messages = getMessages().exploration.timAcceleration;
    const unlocked = acceleration.unlocked;
    this.controlsRow.dataset.timAccelerationUnlocked = unlocked
      ? "true"
      : "false";
    this.timAccelerationControl.hidden = !unlocked;
    this.timAccelerationControl.dataset.state = acceleration.status;
    this.timAccelerationTitle.textContent = messages.title(
      formatForContext(this.currentTim, "main"),
    );
    this.timAccelerationDescription.textContent = messages.activeEffect(
      formatDecimal(acceleration.timPerGameSecond, 1),
      formatInteger(acceleration.speedMultiplier),
    );
    this.timAccelerationToggle.element.dataset.operative =
      acceleration.isApplied ? "true" : "false";
    this.timAccelerationToggle.element.dataset.insufficientTim =
      acceleration.status === "insufficient-tim" ? "true" : "false";
    this.timAccelerationToggle.sync({
      enabled: acceleration.enabled,
      disabled: !unlocked,
      ariaLabel: messages.toggleAria(
        acceleration.enabled,
        acceleration.status === "insufficient-tim",
      ),
    });
    if (!unlocked) {
      this.timAccelerationToggle.clearPointerHover();
    }
  }

  private syncMpSlotPurchase(snapshot: ExplorationDetailsSnapshot): void {
    const messages = getMessages().exploration;
    const purchase = snapshot.nextMpSlotPurchase;
    const availability = purchase.availability;
    this.mpSlotPurchaseButton.disabled = !availability.canExecute;
    this.mpSlotPurchaseButton.setAttribute(
      "aria-disabled",
      availability.canExecute ? "false" : "true",
    );
    this.mpSlotPurchaseCard.dataset.affordable = availability.canExecute
      ? "true"
      : "false";
    this.mpSlotPurchasePrice.textContent =
      purchase.slot === null
        ? messages.mpSlotPurchase.completed
        : purchase.cost === null
          ? messages.mpSlotPurchase.noCost
          : messages.mpSlotPurchase.price(
              formatForContext(purchase.cost, "compact"),
            );
    this.mpSlotPurchaseButton.setAttribute(
      "aria-label",
      purchase.slot === null
        ? messages.mpSlotPurchase.completed
        : this.mpSlotPurchasePrice.textContent,
    );
    if (availability.canExecute) {
      delete this.mpSlotPurchaseButton.dataset.blockedReason;
    } else {
      this.mpSlotPurchaseButton.dataset.blockedReason = availability.reason;
    }

    this.mpSlotPurchaseTooltip.effectValue.textContent =
      purchase.slot === null
        ? messages.mpSlotPurchase.allUnlockedEffect
        : messages.mpSlotPurchase.effect(
            messages.biomes[purchase.slot.biomeId].name,
            formatInteger(purchase.slot.amount),
          );
    this.mpSlotPurchaseTooltip.costValue.textContent =
      purchase.slot === null
        ? messages.mpSlotPurchase.noRemainingCost
        : purchase.cost === null
          ? messages.mpSlotPurchase.noCost
          : messages.cost(formatForContext(purchase.cost, "tooltip"));
    this.mpSlotPurchaseTooltip.stateValue.textContent = availability.canExecute
      ? messages.mpSlotPurchase.states.ready
      : messages.blockedReasons[availability.reason];
    this.mpSlotPurchaseTooltip.root.dataset.affordable = availability.canExecute
      ? "true"
      : "false";
  }
}

function formatExplorationDuration(milliseconds: number): string {
  const seconds = Math.max(0, milliseconds) / 1_000;
  const hasFraction = Math.abs(seconds - Math.round(seconds)) > 1e-9;
  return getMessages().exploration.seconds(
    hasFraction ? formatDecimal(seconds, 1) : formatInteger(seconds),
  );
}

function formatExplorationEffect(effect: ExplorationEffect): string {
  const messages = getMessages().exploration.effects;
  switch (effect.type) {
    case "award-ohov-power":
      return messages.awardOHov(formatForContext(effect.amount, "compact"));
    case "add-exploration-time":
      return messages.addExplorationTime(
        formatExplorationDuration(effect.milliseconds),
      );
    case "multiply-hg":
      return messages.multiplyHg(
        formatForContext(effect.multiplier, "compact"),
      );
    case "raise-biome-level":
      return messages.raiseBiomeLevel(
        formatForContext(effect.amount, "compact"),
      );
  }
}

function formatProgressTurn(progressRatio: number): string {
  return String(Number(progressRatio.toFixed(6)));
}
